import { SideNavigation, Badge, Box, Heading, Select } from '@scale/scaleui-radix';
import { GetCollectionSession } from '@scale/shared/webinarSessions/types';
import { partition } from 'lodash';
import moment from 'moment-timezone';
import { formatSessionDate, isPastSession } from '../helpers';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';

interface Props {
  sessions: GetCollectionSession[];
  selectedSessionId: string;
}

const NavSession = ({
  session,
  selectedSessionId,
}: {
  session: GetCollectionSession;
  selectedSessionId: string;
}) => {
  const match = useRouteMatch();
  const isActive = selectedSessionId === session._id;

  return (
    <SideNavigation.GroupItem key={session._id} active={isActive} asChild>
      <Link to={`${match.url}?session=${session._id}`}>
        {formatSessionDate(session.eventStart)}
        {session.canceled && (
          <Badge size="1" color="error">
            canceled
          </Badge>
        )}
      </Link>
    </SideNavigation.GroupItem>
  );
};

const WebinarSessionSelect = ({
  upcomingSessions,
  pastSessions,
  selectedSessionId,
}: {
  upcomingSessions: GetCollectionSession[];
  pastSessions: GetCollectionSession[];
  selectedSessionId: string;
}) => {
  const match = useRouteMatch();
  const history = useHistory();

  return (
    <Select.Root
      value={selectedSessionId}
      onValueChange={value => {
        history.push(`${match.url}?session=${value}`);
      }}
    >
      <Select.Trigger placeholder="Select a session" />
      <Select.Content>
        {upcomingSessions.length > 0 && (
          <Select.Group>
            <Select.Label>Upcoming sessions</Select.Label>
            {upcomingSessions.map(session => (
              <Select.Item key={session._id} value={session._id}>
                {formatSessionDate(session.eventStart)}
              </Select.Item>
            ))}
          </Select.Group>
        )}
        {upcomingSessions.length > 0 && pastSessions.length > 0 && <Select.Separator />}
        {pastSessions.length > 0 && (
          <Select.Group>
            <Select.Label>Past sessions</Select.Label>
            {pastSessions.map(session => (
              <Select.Item key={session._id} value={session._id}>
                {formatSessionDate(session.eventStart)}
              </Select.Item>
            ))}
          </Select.Group>
        )}
      </Select.Content>
    </Select.Root>
  );
};

const WebinarDetailNavigation = ({ sessions, selectedSessionId }: Props) => {
  const sortedSessions = [...sessions].sort(
    (a, b) => moment(a.eventStart).valueOf() - moment(b.eventStart).valueOf(),
  );

  const [pastSessions, upcomingSessions] = partition(sortedSessions, session =>
    isPastSession(session),
  );

  return (
    <>
      <Box
        p="4"
        display={{
          initial: 'block',
          lg: 'none',
        }}
      >
        <Heading size="4" className="mb-3">
          Selected session
        </Heading>
        <WebinarSessionSelect
          upcomingSessions={upcomingSessions}
          pastSessions={pastSessions}
          selectedSessionId={selectedSessionId}
        />
      </Box>
      <Box
        className="h-full overflow-hidden"
        display={{
          initial: 'none',
          lg: 'block',
        }}
      >
        <SideNavigation.Root>
          <SideNavigation.Content className="h-full overflow-auto" expandedWidth="fit-content">
            <SideNavigation.Items gap="2">
              {upcomingSessions.length > 0 && (
                <SideNavigation.Group label="Upcoming sessions" defaultOpen>
                  {upcomingSessions.map(session => (
                    <NavSession
                      key={session._id}
                      session={session}
                      selectedSessionId={selectedSessionId}
                    />
                  ))}
                </SideNavigation.Group>
              )}

              {pastSessions.length > 0 && (
                <SideNavigation.Group label="Past sessions" defaultOpen>
                  {pastSessions.map(session => (
                    <NavSession
                      key={session._id}
                      session={session}
                      selectedSessionId={selectedSessionId}
                    />
                  ))}
                </SideNavigation.Group>
              )}
            </SideNavigation.Items>
          </SideNavigation.Content>
        </SideNavigation.Root>
      </Box>
    </>
  );
};

export default WebinarDetailNavigation;
